
import type { NavKey } from "../Sidebar";

export type SearchHit = {
  id: string;
  type: string; 
  title: string;
  subtitle?: string;
  navKey: NavKey; 
  sourceId: string; 
};

export function runGlobalSearch(query: string, dateFrom?: string, dateTo?: string): SearchHit[] {
  if (!query || query.trim().length === 0) return [];
  
  const q = query.toLowerCase();
  const results: SearchHit[] = [];

  const getItems = (key: string) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };

  // Helper: Date Check
  const checkDate = (itemDate?: string) => {
      if (!dateFrom && !dateTo) return true;
      if (!itemDate) return false;
      const d = itemDate.split("T")[0]; // ensure yyyy-mm-dd
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
  };

  // 1. Contracts
  const contracts = getItems("legalops.contracts.v1");
  contracts.forEach((c: any) => {
    const relevantDate = c.signDate || c.periodStart || c.createdAt;
    if (!checkDate(relevantDate)) return;

    if (c.title?.toLowerCase().includes(q) || c.contractNo?.toLowerCase().includes(q) || c.counterparty?.toLowerCase().includes(q)) {
      results.push({
        id: `ct-${c.id}`,
        type: "合約",
        title: c.title,
        subtitle: `${c.contractNo} | ${c.counterparty}`,
        navKey: "contracts",
        sourceId: c.id
      });
    }
  });

  // 2. Cases
  const cases = getItems("legalops.cases.v1");
  cases.forEach((c: any) => {
    const relevantDate = c.openDate || c.createdAt;
    if (!checkDate(relevantDate)) return;

    if (c.title?.toLowerCase().includes(q) || c.counterparty?.toLowerCase().includes(q) || c.ourDept?.toLowerCase().includes(q)) {
      results.push({
        id: `cs-${c.id}`,
        type: "案件",
        title: c.title,
        subtitle: `${c.caseType} | ${c.counterparty}`,
        navKey: "legalMatters",
        sourceId: c.id
      });
    }
  });

  // 3. Disputes
  const disputes = getItems("legalops.disputes.v1");
  disputes.forEach((d: any) => {
    const relevantDate = d.receivedDate || d.createdAt;
    if (!checkDate(relevantDate)) return;

    if (d.title?.toLowerCase().includes(q) || d.counterparty?.toLowerCase().includes(q)) {
      results.push({
        id: `dp-${d.id}`,
        type: "爭議",
        title: d.title,
        subtitle: `${d.disputeType} | ${d.counterparty}`,
        navKey: "legalMatters",
        sourceId: d.id
      });
    }
  });

  // 4. Brand Assets
  const brands = getItems("legalops.brandAssets.v1");
  brands.forEach((b: any) => {
    const relevantDate = b.expiryDate || b.createdAt;
    if (!checkDate(relevantDate)) return;

    if (b.name?.toLowerCase().includes(q) || b.brand?.toLowerCase().includes(q)) {
      results.push({
        id: `ba-${b.id}`,
        type: "品牌資產",
        title: b.name,
        subtitle: `${b.brand} / ${b.series}`,
        navKey: "brandAssets",
        sourceId: b.id
      });
    }
  });

  // 5. Lawyers
  const lawyers = getItems("legalops.lawyers.v1");
  lawyers.forEach((l: any) => {
    const relevantDate = l.createdAt;
    if (!checkDate(relevantDate)) return;

    if (l.lawFirm?.toLowerCase().includes(q) || l.lawyerName?.toLowerCase().includes(q)) {
      results.push({
        id: `lw-${l.id}`,
        type: "顧問律師",
        title: l.lawFirm,
        subtitle: `${l.lawyerName} | ${l.specialty}`,
        navKey: "lawyerCoop",
        sourceId: l.id
      });
    }
  });

  // 6. Tasks
  const tasks = getItems("legalops.tasks.v1");
  tasks.forEach((t: any) => {
    const relevantDate = t.dueDate || t.createdAt;
    if (!checkDate(relevantDate)) return;

    if (t.title?.toLowerCase().includes(q) || t.assignee?.toLowerCase().includes(q)) {
      results.push({
        id: `tk-${t.id}`,
        type: "待辦",
        title: t.title,
        subtitle: `負責人: ${t.assignee}`,
        navKey: "tasksAndLogs",
        sourceId: t.id
      });
    }
  });

  // 7. Governance Core
  const govCore = getItems("legalops.governanceCoreDocs.v1");
  govCore.forEach((g: any) => {
    const relevantDate = g.effectiveDate || g.createdAt;
    if (!checkDate(relevantDate)) return;

    if (g.title?.toLowerCase().includes(q) || g.docNo?.toLowerCase().includes(q)) {
      results.push({
        id: `gc-${g.id}`,
        type: "核心文件",
        title: g.title,
        subtitle: g.docNo,
        navKey: "governanceCoreDocs",
        sourceId: g.id
      });
    }
  });

  // 8. Templates
  const tContracts = getItems("legalops.templates.contracts.v1");
  const tClauses = getItems("legalops.templates.clauses.v1");
  [...tContracts, ...tClauses].forEach((t: any) => {
    const relevantDate = t.createdAt;
    if (!checkDate(relevantDate)) return;

    if (t.title?.toLowerCase().includes(q) || t.category?.toLowerCase().includes(q)) {
      results.push({
        id: `tm-${t.id}`,
        type: "範本",
        title: t.title,
        subtitle: t.category,
        navKey: "templates",
        sourceId: t.id
      });
    }
  });
  
  // 9. Government Correspondence
  const govs = getItems("legalops.govCorrespondence.v1");
  govs.forEach((g: any) => {
    const relevantDate = g.date || g.createdAt;
    if (!checkDate(relevantDate)) return;

    if (g.subject?.toLowerCase().includes(q) || g.agencyName?.toLowerCase().includes(q) || g.incomingRefNo?.toLowerCase().includes(q)) {
      results.push({
        id: `gv-${g.id}`,
        type: "公文",
        title: g.subject,
        subtitle: `${g.agencyName} | ${g.incomingRefNo}`,
        navKey: "govCorrespondence",
        sourceId: g.id
      });
    }
  });

  return results.slice(0, 20);
}
